import { Plural, Trans, useLingui } from '@lingui/react/macro';
import { AlertTriangle, CheckCircle2, Clock, Loader2, Play, Rocket, Settings, X, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useStartCollisionDetection, useTaskProgress } from '@/api/hooks';
import { Button } from '@/components/ui/button';
import type { ProjectDetail, QueueTaskInfo } from '@/types/api';
import { apiErrorMessage } from '@/utils/apiError';
import { getProgressMessage } from '@/utils/taskProgress';

const getRunningMsg = (progress: QueueTaskInfo | null): React.ReactNode => {
  const { label, percent } = getProgressMessage(progress);
  const step = progress?.progress_step ?? null;

  return (
    <div className="flex flex-col gap-1.5">
      <span>{label}</span>
      {step !== null && (
        <div className="relative h-1.5 w-full rounded-full bg-current/20">
          <div
            className="absolute inset-y-0 left-0 rounded-full bg-current transition-[width] duration-500"
            style={{ width: `${percent}%` }}
          />
        </div>
      )}
    </div>
  );
};

export const SimBannerContent = ({
  projectId,
  state,
  collisionCount,
  backgroundTaskId,
  simulationErrorMessage,
  onShowCancelConfirm,
  cancelDetectionIsPending,
}: {
  projectId: string;
  state: ProjectDetail['simulation_state'];
  collisionCount?: number | null;
  backgroundTaskId?: string | null;
  simulationErrorMessage?: string | null;
  onShowCancelConfirm: (show: boolean) => void;
  cancelDetectionIsPending: boolean;
}): React.ReactElement => {
  const startDetectionMutation = useStartCollisionDetection(projectId);
  const progress = useTaskProgress(backgroundTaskId ?? null);
  const { t } = useLingui();

  const handleRunDetection = (): void => {
    startDetectionMutation.mutate(undefined, {
      onError: (e) => toast.error(apiErrorMessage(e, t`Failed to start collision detection.`)),
    });
  };

  const handleCancelDetection = (): void => {
    onShowCancelConfirm(true);
  };

  const getCollisionsTitle = (): React.ReactNode => {
    const count = collisionCount ?? 0;
    return <Plural value={count} one="# collision detected" other="# collisions detected" />;
  };

  type BannerCfg = { icon: React.ReactNode; title: React.ReactNode; msg: React.ReactNode };
  const configMap = new Map<string, BannerCfg>([
    [
      'incomplete',
      {
        icon: <Settings size={42} />,
        title: <Trans>Incomplete inputs</Trans>,
        msg: <Trans>Add all required inputs to start detection.</Trans>,
      },
    ],
    [
      'ready',
      {
        icon: <Rocket size={42} />,
        title: <Trans>Ready to run</Trans>,
        msg: <Trans>All inputs are ready. Start collision detection.</Trans>,
      },
    ],
    [
      'running',
      {
        icon: <Clock size={42} />,
        title: <Trans>Simulation running…</Trans>,
        msg: getRunningMsg(progress),
      },
    ],
    [
      'complete_ok',
      {
        icon: <CheckCircle2 size={42} />,
        title: <Trans>No collisions</Trans>,
        msg: <Trans>Simulation completed successfully. No collisions found.</Trans>,
      },
    ],
    [
      'complete_collisions',
      {
        icon: <AlertTriangle size={42} />,
        title: getCollisionsTitle(),
        msg: <Trans>Review the collision list below.</Trans>,
      },
    ],
    [
      'error',
      {
        icon: <XCircle size={48} />,
        title: <Trans>Simulation failed</Trans>,
        msg: simulationErrorMessage ?? <Trans>An error occurred during simulation.</Trans>,
      },
    ],
  ]);
  const c = configMap.get(state) ?? (configMap.get('incomplete') as BannerCfg);

  return (
    <div role="status" className="flex items-center gap-3 font-medium w-full">
      <div className="flex items-center justify-center">{c.icon}</div>
      <div className="flex-1">
        <span className="font-semibold">{c.title}</span>
        <div className="mt-0.5 text-sm opacity-80">{c.msg}</div>
      </div>
      {state === 'ready' && (
        <Button variant="default" onClick={handleRunDetection} disabled={startDetectionMutation.isPending}>
          {startDetectionMutation.isPending ? (
            <Loader2 data-icon="inline-start" className="animate-spin" />
          ) : (
            <Play data-icon="inline-start" />
          )}{' '}
          <Trans>Run collision detection</Trans>
        </Button>
      )}
      {state === 'running' && (
        <Button
          variant="outline"
          className="hover:border-red-300 hover:text-red-600"
          onClick={handleCancelDetection}
          disabled={cancelDetectionIsPending}
        >
          {cancelDetectionIsPending ? (
            <Loader2 data-icon="inline-start" className="animate-spin" />
          ) : (
            <X data-icon="inline-start" />
          )}{' '}
          <Trans>Cancel</Trans>
        </Button>
      )}
    </div>
  );
};
